import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { contentColumn, eyebrow, focusRing, meta } from '../../styles/visualLanguage';
import { AdvisoryCalloutState } from './components/AdvisoryCalloutState';
import { AdvisoryIntakeScreen } from './components/AdvisoryIntakeScreen';
import { isPeriodConfirmationDemoEnabled } from './AdvisoryPeriodConfirmationDemoPage';
import type { AdvisoryDemoEnvironment } from './AdvisoryCompetitorAnalysisDemoPage';
import {
  AdvisoryUnifiedReviewScreen,
  type AdvisoryUnifiedReviewDraft,
  type AdvisoryReviewSegment,
  type AdvisoryReviewConflict,
  type AdvisoryReviewField,
  type AdvisoryUnifiedReviewScreenProps,
} from './components/AdvisoryUnifiedReviewScreen';

const scenarios = ['ordinary', 'competitor', 'acquisition'] as const;
const states = [
  'initial-preparation',
  'ready',
  'missing',
  'multiple',
  'defaulted',
  'failed',
  'conflicts',
  'kind-conflict',
  'saving',
  'refining',
  'preparing',
  'error',
] as const;
type Scenario = (typeof scenarios)[number];
type PreviewState = (typeof states)[number];
export const isUnifiedReviewDemoEnabled = isPeriodConfirmationDemoEnabled;

function ReviewFixture({ scenario, state }: { scenario: Scenario; state: PreviewState }) {
  const { t } = useUntypedTranslation();
  const [preparationParams] = useSearchParams();
  const [draft, setDraft] = useState<AdvisoryUnifiedReviewDraft>({
    subjectCompany: state === 'missing' || state === 'multiple' ? '' : 'Aurora Mobility',
    counterparties:
      scenario === 'ordinary' || state === 'missing' || state === 'multiple'
        ? []
        : scenario === 'competitor'
        ? ['Northstar Motors', 'Vertex Automotive']
        : ['Helios Systems'],
    reportingPeriod:
      state === 'missing'
        ? null
        : state === 'failed' || state === 'multiple'
        ? {
            year: new Date().getFullYear(),
            quarter: null,
            endQuarter: null,
            yearType: 'calendar',
            fiscalStartMonth: 1,
          }
        : {
            year: 2027,
            quarter: 1,
            endQuarter: 3,
            yearType: 'fiscal',
            fiscalStartMonth: state === 'defaulted' ? 2 : 4,
          },
  });
  const [periodState, setPeriodState] = useState<
    NonNullable<AdvisoryUnifiedReviewScreenProps['periodState']>
  >(
    state === 'missing'
      ? 'missing'
      : state === 'multiple' || state === 'failed'
      ? 'failed'
      : state === 'defaulted'
      ? 'defaulted'
      : 'found'
  );
  const [feedback, setFeedback] = useState(
    state === 'refining' ? t('advisory:review.previewFeedback') : ''
  );
  const [captured, setCaptured] = useState<'refined' | 'confirmed' | null>(null);
  const [conflicts, setConflicts] = useState<AdvisoryReviewConflict[]>(
    state === 'conflicts'
      ? [{ field: 'subjectCompany', mine: 'Aurora Mobility', suggestion: 'Aurora Group' }]
      : state === 'kind-conflict'
      ? [
          {
            field: 'counterpartyKind',
            mine: t('advisory:review.competitors'),
            suggestion: t('advisory:review.targets'),
          },
        ]
      : []
  );
  const [kind, setKind] = useState<'competitor' | 'acquisition target' | null>(
    scenario === 'ordinary' ? null : scenario === 'competitor' ? 'competitor' : 'acquisition target'
  );
  const statement: AdvisoryReviewSegment[] = [
    {
      type: 'text',
      text: t(
        'advisory:review.previewStatement.' + (captured === 'refined' ? 'refined' : scenario)
      ),
    },
    { type: 'field', field: 'subjectCompany' },
    ...(kind
      ? [
          {
            type: 'text' as const,
            text: t(
              'advisory:review.previewStatement.' +
                (kind === 'competitor' ? 'against' : 'acquiring')
            ),
          },
          { type: 'field' as const, field: 'counterparties' as const },
        ]
      : []),
    { type: 'text', text: t('advisory:review.previewStatement.period') },
    { type: 'field', field: 'reportingPeriod' },
    { type: 'text', text: t('advisory:review.previewStatement.end') },
  ];
  const resolve = (field: AdvisoryReviewField, choice: 'keep' | 'suggestion') => {
    if (choice === 'suggestion' && field === 'subjectCompany')
      setDraft(current => ({ ...current, subjectCompany: 'Aurora Group' }));
    if (choice === 'suggestion' && field === 'counterpartyKind') {
      setKind('acquisition target');
      setDraft(current => ({ ...current, counterparties: ['Helios Systems'] }));
    }
    setConflicts(current => current.filter(conflict => conflict.field !== field));
  };
  if (state === 'initial-preparation') {
    const stage = preparationParams.get('stage');
    return (
      <AdvisoryIntakeScreen
        question="Should Apple acquire Anthropic?"
        readOnly
        busy
        animateEntry
        stage={
          stage === 'understandingDetails' || stage === 'shapingEngagement' ? stage : 'preparing'
        }
      />
    );
  }
  return (
    <>
      {captured ? (
        <Typography
          role="status"
          data-testid="advisory-review-preview-captured"
          sx={[contentColumn, meta, { paddingTop: 2 }]}
        >
          {t('advisory:review.previewCaptured.' + captured)}
        </Typography>
      ) : null}
      <AdvisoryUnifiedReviewScreen
        draft={draft}
        statement={statement}
        counterpartyKind={kind}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        onChange={next => {
          if (next.reportingPeriod !== draft.reportingPeriod) setPeriodState('found');
          setDraft(next);
        }}
        onRefine={() => {
          setCaptured('refined');
          setFeedback('');
        }}
        onConfirm={() => setCaptured('confirmed')}
        periodState={periodState}
        saving={state === 'saving'}
        refining={state === 'refining'}
        preparing={state === 'preparing'}
        refinementsRemaining={captured === 'refined' ? 2 : 3}
        conflicts={conflicts}
        onResolveConflict={resolve}
        errorI18nKey={state === 'error' ? 'advisory:review.previewError' : null}
      />
    </>
  );
}

export function AdvisoryUnifiedReviewDemoPage({
  environment = {
    nodeEnv: process.env.NODE_ENV,
    firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  },
}: {
  environment?: AdvisoryDemoEnvironment;
}) {
  const { t } = useUntypedTranslation();
  const [params, setParams] = useSearchParams();
  const scenario = scenarios.find(value => value === params.get('scenario')) ?? 'ordinary';
  const state = states.find(value => value === params.get('state')) ?? 'ready';
  if (!isUnifiedReviewDemoEnabled(environment))
    return (
      <AdvisoryCalloutState
        tone="info"
        headingKey="advisory:callout.absent.heading"
        bodyKey="advisory:errors.notFound"
      />
    );
  return (
    <Box data-testid="advisory-unified-review-preview">
      <Box sx={theme => ({ paddingX: 2.5, backgroundColor: theme.palette.background.default })}>
        <Box
          sx={[
            contentColumn,
            theme => ({
              paddingY: 1.5,
              borderBottom: '1px solid ' + theme.visualPrimitives.border,
            }),
          ]}
        >
          <Typography sx={[eyebrow]}>{t('advisory:period.previewBadge')}</Typography>
          {(
            [
              ['scenario', scenarios],
              ['state', states],
            ] as const
          ).map(([field, values]) => (
            <Box
              key={field}
              role="group"
              aria-label={t('advisory:review.previewGroup.' + field)}
              sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, marginTop: 0.5 }}
            >
              {values.map(value => (
                <Button
                  key={value}
                  size="small"
                  variant="text"
                  disableRipple
                  aria-pressed={(field === 'scenario' ? scenario : state) === value}
                  onClick={() => setParams({ scenario, state, [field]: value }, { replace: true })}
                  sx={[
                    meta,
                    focusRing,
                    {
                      minWidth: 0,
                      padding: 0,
                      textTransform: 'none',
                      '&[aria-pressed="true"]': { textDecoration: 'underline' },
                    },
                  ]}
                >
                  {t('advisory:review.previewChoice.' + value)}
                </Button>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      <ReviewFixture key={scenario + ':' + state} scenario={scenario} state={state} />
    </Box>
  );
}
